/**
 * Detalhe da proposta → «Seguimento no Odoo» (HANDOFF S3): estado (com a cor
 * do selo da lista), etapa e vendedor da lead, atividades abertas (atrasadas
 * a vermelho), a última ação do comercial e «Abrir no Odoo». Lido ao abrir;
 * «Atualizar» volta a ler. Só para propostas com lead do Odoo.
 */
import { useCallback, useEffect, useState } from "react"
import { ExternalLink, Loader2, RefreshCw } from "lucide-react"
import type { AppShell } from "@/App"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { FOLLOWUP_LABEL, FOLLOWUP_VARIANT, getFollowups, type Followup } from "@/lib/followup"
import { cn } from "@/lib/utils"

const when = (iso: string | null) =>
  iso ? new Date(iso).toLocaleString("pt-PT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : "—"
const date = (d: string | null) => (d ? new Date(`${d.slice(0, 10)}T12:00:00`).toLocaleDateString("pt-PT", { day: "2-digit", month: "2-digit", year: "numeric" }) : "—")

export function FollowupCard({ shell, proposal }: { shell: AppShell | null; proposal: { id: string; odoo_lead_id?: unknown } }) {
  const hasLead = Number(proposal.odoo_lead_id) > 0
  const [f, setF] = useState<Followup | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)

  const load = useCallback(() => {
    if (!shell || !hasLead) return
    setLoading(true)
    setError(null)
    getFollowups(shell, [proposal.id])
      .then((items) => setF(items[proposal.id] ?? null))
      .catch((e) => setError(e instanceof Error ? e.message : "Falha a ler o Odoo"))
      .finally(() => setLoading(false))
  }, [shell, hasLead, proposal.id])

  useEffect(() => { load() }, [load])

  if (!hasLead) return null
  const statusLine = f
    ? f.state === "none"
      ? `Prazo passou${f.overdue_days ? ` há ${f.overdue_days} ${f.overdue_days === 1 ? "dia" : "dias"}` : ""} (${when(f.due_at)})`
      : f.state === "pending"
        ? `Prazo: ${when(f.due_at)}`
        : f.state === "closed"
          ? f.outcome === "won" ? "Ganha no Odoo" : "Perdida no Odoo"
          : f.state === "active" && f.first_action_at
            ? `1.ª ação ${when(f.first_action_at)}${f.on_time === false ? " — fora do prazo" : ""}`
            : null
    : null

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between gap-2 pb-2">
        <div className="flex flex-wrap items-center gap-2">
          <CardTitle className="text-base">Seguimento no Odoo</CardTitle>
          {f && <Badge variant={FOLLOWUP_VARIANT[f.state]}>{FOLLOWUP_LABEL[f.state]}</Badge>}
          {statusLine && <span className="text-xs text-muted-foreground">{statusLine}</span>}
        </div>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={load} disabled={loading} aria-label="Atualizar seguimento">
            {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
            Atualizar
          </Button>
          {f?.odoo_url && (
            <Button asChild variant="outline" size="sm">
              <a href={f.odoo_url} target="_blank" rel="noreferrer">
                Abrir no Odoo <ExternalLink className="h-3.5 w-3.5" />
              </a>
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="text-sm">
        {error && <p className="text-destructive">{error}</p>}
        {!f && !error && loading && <p className="text-muted-foreground">A ler o Odoo…</p>}
        {f?.messages_truncated && (
          <p className="mb-2 text-xs text-amber-600">
            Esta lead tem mais mensagens no Odoo do que a app lê de uma vez — o seguimento pode estar incompleto; confirme no Odoo.
          </p>
        )}
        {f && f.state === "unknown" && (
          <p className="text-muted-foreground">A lead {f.lead_id} não foi encontrada no Odoo (apagada ou sem acesso).</p>
        )}
        {f && f.state !== "unknown" && (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            <div className="space-y-1">
              <div className="text-xs text-muted-foreground">Etapa</div>
              <div>{f.stage_name ?? "—"}</div>
              <div className="pt-1 text-xs text-muted-foreground">Vendedor no Odoo</div>
              <div>{f.salesperson?.name ?? "Sem vendedor"}</div>
            </div>
            <div className="space-y-1">
              <div className="text-xs text-muted-foreground">Atividades abertas</div>
              {f.open_activities.length === 0 ? (
                <div className="text-muted-foreground">Nenhuma</div>
              ) : (
                <ul className="space-y-1">
                  {f.open_activities.map((a, i) => (
                    <li key={i}>
                      <div>{a.summary}</div>
                      <div className={cn("text-xs", a.overdue ? "font-medium text-red-600" : "text-muted-foreground")}>
                        {a.overdue ? "Atrasada — " : ""}prazo {date(a.deadline)}{a.user ? ` · ${a.user}` : ""}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div className="space-y-1">
              <div className="text-xs text-muted-foreground">Última ação do comercial</div>
              {f.last_action ? (
                <>
                  <div>{f.last_action.text}</div>
                  <div className="text-xs text-muted-foreground">
                    {when(f.last_action.at)}{f.last_action.author ? ` · ${f.last_action.author}` : ""}{f.last_action.source === "app" ? " · na app" : ""}
                  </div>
                </>
              ) : (
                <div className="text-muted-foreground">Nenhuma desde a criação da proposta</div>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  )
}
